/**
 * @fileoverview Basic Movement Test Suite
 *
 * Tests for standard piece movement across all 13 piece types.
 */

import { PieceType } from "../../zodiacEngine/types";
import { IntegrationTest, TestSuite } from "../types";

/**
 * Standard movement tests - one per piece type
 *
 * Note: Penguin moved to "Bonus Turns" category since slides grant bonus turn for rotation
 *
 * Board is 5 columns (A-E) x 8 rows (1-8)
 * FEN is column-by-column, bottom-to-top per column
 * Example: "8/8/BK6ck/8/8" means:
 *   - Col A: 8 empty
 *   - Col B: 8 empty
 *   - Col C: Bear+King at row 1, 6 empty above, Crane+king at row 8
 *   - Col D: 8 empty
 *   - Col E: 8 empty
 */
export const basicMoveTests: IntegrationTest[] = [
  // ============ BEAR ============
  // Movement: 1 square any direction
  {
    name: "Bear moves one square forward",
    description: "Light Bear moves from C1 to C2",
    inputFEN: "8/8/BK6ck/8/8",
    lightKing: PieceType.Bear,
    darkKing: PieceType.Crane,
    move: "Light Bear C1->C2",
    expectedFEN: "8/8/1BK5ck/8/8",
    expectedDelta: {
      pieceMoved: "Light Bear moved [C1 -> C2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ CRANE ============
  // Movement: 1-2 squares straight (orthogonal)
  {
    name: "Crane moves two squares forward",
    description: "Light Crane moves from C1 to C3",
    inputFEN: "8/8/CK6bk/8/8",
    lightKing: PieceType.Crane,
    darkKing: PieceType.Bear,
    move: "Light Crane C1->C3",
    expectedFEN: "8/8/2CK4bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Crane moved [C1 -> C3]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ FROG ============
  // Movement: 2 squares diagonal
  {
    name: "Frog moves two squares diagonally",
    description: "Light Frog moves from C2 to E4",
    inputFEN: "8/8/1FK5bk/8/8",
    lightKing: PieceType.Frog,
    darkKing: PieceType.Bear,
    move: "Light Frog C2->E4",
    expectedFEN: "8/8/7bk/8/3FK4",
    expectedDelta: {
      pieceMoved: "Light Frog moved [C2 -> E4]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ FUNGI ============
  // Movement: 1 square any direction
  {
    name: "Fungi moves one square forward",
    description: "Light Fungi moves from C1 to C2",
    inputFEN: "8/8/UK6bk/8/8",
    lightKing: PieceType.Fungi,
    darkKing: PieceType.Bear,
    move: "Light Fungi C1->C2",
    expectedFEN: "8/8/1UK5bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Fungi moved [C1 -> C2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ GOAT ============
  // Movement: 1 square any direction (also has Traverse)
  {
    name: "Goat moves one square forward",
    description: "Light Goat moves from C1 to C2",
    inputFEN: "8/8/GK6bk/8/8",
    lightKing: PieceType.Goat,
    darkKing: PieceType.Bear,
    move: "Light Goat C1->C2",
    expectedFEN: "8/8/1GK5bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Goat moved [C1 -> C2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ MONKEY ============
  // Movement: 1 square any direction (also has Leap)
  {
    name: "Monkey moves one square forward",
    description: "Light Monkey moves from C1 to C2",
    inputFEN: "8/8/MK6bk/8/8",
    lightKing: PieceType.Monkey,
    darkKing: PieceType.Bear,
    move: "Light Monkey C1->C2",
    expectedFEN: "8/8/1MK5bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Monkey moved [C1 -> C2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ OCTOPUS ============
  // Movement: 1 square any direction
  {
    name: "Octopus moves one square forward",
    description: "Light Octopus moves from C1 to C2",
    inputFEN: "8/8/OK6bk/8/8",
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Bear,
    move: "Light Octopus C1->C2",
    expectedFEN: "8/8/1OK5bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Octopus moved [C1 -> C2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

    // ============ Penguin ============
  // Movement: rotate direction
  {
    name: "Penguin rotates from down to up!",
    description: "Light Penguin rotates to face up",
    inputFEN: "8/8/P↓K6bk/8/8",
    lightKing: PieceType.Penguin,
    darkKing: PieceType.Bear,
    move: "Light Penguin C1->C2:rotate",
    expectedFEN: "8/8/PK↑6bk/8/8",
    expectedDelta: {
      pieceMoved: "Penguin rotated up in place [C1]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
      statusChanged: false,
    },
  },

  // ============ RHINO ============
  // Movement: 2 forward OR 1 back-diagonal
  {
    name: "Rhino moves two squares forward",
    description: "Light Rhino moves from C1 to C3",
    inputFEN: "8/8/RK6bk/8/8",
    lightKing: PieceType.Rhino,
    darkKing: PieceType.Bear,
    move: "Light Rhino C1->C3",
    expectedFEN: "8/8/2RK4bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Rhino moved [C1 -> C3]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ SNAKE ============
  // Movement: 2 squares orthogonal
  {
    name: "Snake moves two squares forward",
    description: "Light Snake moves from C1 to C3",
    inputFEN: "8/8/SK6bk/8/8",
    lightKing: PieceType.Snake,
    darkKing: PieceType.Bear,
    move: "Light Snake C1->C3",
    expectedFEN: "8/8/2SK4bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Snake moved [C1 -> C3]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // Snake with Virus - infected snake can still move
  {
    name: "Snake with Virus moves two squares forward",
    description: "Infected Light Snake moves from E1 to E3",
    inputFEN: "8/8/8/8/SK+V6bk",
    lightKing: PieceType.Snake,
    darkKing: PieceType.Bear,
    move: "Light Snake E1->E3",
    expectedFEN: "8/8/8/8/2SK+V4bk",
    expectedDelta: {
      pieceMoved: "Light Snake moved [E1 -> E3]",
      statusChanged: false,
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ TREE ============
  // Movement: 1 square any direction
  {
    name: "Tree moves one square forward",
    description: "Light Tree moves from C1 to C2",
    inputFEN: "8/8/TK6bk/8/8",
    lightKing: PieceType.Tree,
    darkKing: PieceType.Bear,
    move: "Light Tree C1->C2",
    expectedFEN: "8/8/1TK5bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Tree moved [C1 -> C2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ WOLF ============
  // Movement: 1 square any direction (has Lone special when last piece)
  // Note: Needs another Light piece to prevent Lone Wolf L-movement
  {
    name: "Wolf moves one square diagonally",
    description: "Light Wolf moves from C1 to B3 (with Monkey to prevent Lone mode)",
    inputFEN: "M7/8/WK6bk/8/8",
    lightKing: PieceType.Wolf,
    darkKing: PieceType.Bear,
    move: "Light Wolf C1->B3",
    expectedFEN: "M7/2WK5/7bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Wolf moved [C1 -> B3]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ VIRUS ============
  // Movement: 2 squares to animals only (cannot be king, so use different setup)
  // Note: Virus moves ONTO the animal (infects it), so they share the same square
  // Note: Dark Bear at D1 is only 1 square from C1, but E1 is on-board so no edge rule applies
  // Therefore no valid infection targets from C1, so no bonus turn
  {
    name: "Virus moves two squares to animal",
    description: "Light Virus moves from A1 to C1 (infects Bear)",
    inputFEN: "V7/8/BK6bk/8/8",
    lightKing: PieceType.Bear,
    darkKing: PieceType.Bear,
    move: "Light Virus A1->C1",
    expectedFEN: "8/8/B♛+V6b♕/8/8",
    expectedDelta: {
      pieceMoved: "Light Virus infected Light Bear [A1 -> C1]",
      statusChanged: true, // Virus stacks on Bear
      turnChanged: true, // No bonus turn - no valid targets from C1 (D1 is 1 square, E1 is empty)
      bonusTurn: false,
      gameEnded: false,
    },
  },
];

export const basicMovesSuite: TestSuite = {
  name: "Basic Movements",
  description: "Tests for standard piece movement (12 pieces)",
  tests: basicMoveTests,
};

/**
 * Special movement tests - piece abilities beyond standard movement
 *
 * Tests for unique abilities like Lilypad, Traverse, Leap, Lane, etc.
 */
export const specialMoveTests: IntegrationTest[] = [
  // ============ CRANE ============
  // Crane can climb into trees (house move)
  {
    name: "Crane flies into tree",
    description: "Light Crane climbs into tree at D1",
    inputFEN: "8/8/CK6bk/T7/8",
    lightKing: PieceType.Crane,
    darkKing: PieceType.Bear,
    move: "Light Crane C1->D1",
    expectedFEN: "8/8/7bk/T+CK7/8",
    expectedDelta: {
      pieceMoved: "Light Crane flew into Light Tree [C1 -> D1]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ FROG ============
  // Frog can climb walls (move to edge when blocked)
  {
    name: "Frog wall climb",
    description: "Light Frog climbs wall when 2-diagonal blocked",
    inputFEN: "2FK5/8/7bk/8/8",
    lightKing: PieceType.Frog,
    darkKing: PieceType.Bear,
    move: "Light Frog A3->A4",
    expectedFEN: "3FK4/8/7bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Frog moved [A3 -> A4]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // Frog lilypad - first move can go anywhere
  {
    name: "Frog lilypad jump",
    description: "Light Frog uses lilypad to jump from E1 to A3",
    inputFEN: "8/8/7bk/8/F*K7",
    lightKing: PieceType.Frog,
    darkKing: PieceType.Bear,
    move: "Light Frog E1->A3",
    expectedFEN: "2FK5/8/7bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Frog leaped from its Lilypad [E1 -> A3]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // Frog blocked by wall
  {
    name: "Frog wall block",
    description: "Frog at edge can only move 1 square when 2-diagonal off board",
    inputFEN: "8/1FK6/7bk/8/8",
    lightKing: PieceType.Frog,
    darkKing: PieceType.Bear,
    move: "Light Frog B2->A3",
    expectedFEN: "2FK5/8/7bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Frog moved [B2 -> A3]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // Frog blocked by piece
  {
    name: "Frog piece block",
    description: "Frog blocked by piece, moves only 1 square diagonal",
    inputFEN: "3B4/8/1FK5bk/8/8",
    lightKing: PieceType.Frog,
    darkKing: PieceType.Bear,
    move: "Light Frog C2->B3",
    expectedFEN: "3B4/2FK5/7bk/8/8",
    expectedDelta: {
      pieceMoved: "Light Frog moved [C2 -> B3]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ GOAT ============
  // Goat traverse - climb over pieces
  {
    name: "Goat traverse",
    description: "Light Goat climbs over piece to reach destination",
    inputFEN: "7bk/8/GK7/1B6/8",
    lightKing: PieceType.Goat,
    darkKing: PieceType.Bear,
    move: "Light Goat C1->C3",
    expectedFEN: "7b♕/8/2GK5/1B6/8",
    expectedDelta: {
      pieceMoved: "Light Goat traversed [C1 -> C3]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ MONKEY ============
  // Monkey leap - 6 squares with dice roll of 6
  {
    name: "Monkey triple leap",
    description: "Light Monkey leaps 6 squares",
    inputFEN: "7bk/8/MKB1C1G2/8/8",
    lightKing: PieceType.Monkey,
    darkKing: PieceType.Bear,
    move: "Light Monkey C1->C7:leap",
    expectedFEN: "7bk/8/1B1C1GMK1/8/8",
    expectedDelta: {
      pieceMoved: "Light Monkey leaped over Light Bear, Light Crane and Light Goat [C1 -> C7]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ RHINO ============
  // Rhino lane - swap positions on home rank
  // NOTE: ○ marker required to indicate Rhino hasn't moved (lane ability available)
  {
    name: "Rhino lane",
    description: "Light Rhino swaps with Light Bear",
    inputFEN: "7bk/8/R○K7/B7/8",
    lightKing: PieceType.Rhino,
    darkKing: PieceType.Bear,
    move: "Light Rhino C1->D1:lane",
    expectedFEN: "7bk/8/B7/RK7/8",
    expectedDelta: {
      pieceMoved: "Light Rhino used Lane to swap places with Light Bear [C1 -> D1]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // Rhino back step - 1 square back-diagonal
  {
    name: "Rhino back step",
    description: "Light Rhino moves 1 square back-diagonal",
    inputFEN: "7bk/8/1RK*6/8/8",
    lightKing: PieceType.Rhino,
    darkKing: PieceType.Bear,
    move: "Light Rhino C2->D1",
    expectedFEN: "7bk/8/8/RK7/8",
    expectedDelta: {
      pieceMoved: "Light Rhino moved [C2 -> D1]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ SNAKE ============
  // Snake blocked by wall - can only move 1 square instead of 2
  {
    name: "Snake wall block",
    description: "Snake at edge moves 1 square when 2-square move is off board",
    inputFEN: "8/1SK*6/8/8/7bk",
    lightKing: PieceType.Snake,
    darkKing: PieceType.Bear,
    move: "Light Snake B2->A2",
    expectedFEN: "1SK*6/8/8/8/7bk",
    expectedDelta: {
      pieceMoved: "Light Snake moved [B2 -> A2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // Snake blocked by piece - moves only 1 square
  {
    name: "Snake piece block",
    description: "Snake only moves 1 square when blocked by piece",
    inputFEN: "1B6/8/1SK*6/8/7bk",
    lightKing: PieceType.Snake,
    darkKing: PieceType.Bear,
    move: "Light Snake C2->B2",
    expectedFEN: "1B6/1SK*6/8/8/7bk",
    expectedDelta: {
      pieceMoved: "Light Snake moved [C2 -> B2]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ TREE ============
  // Tree side move - can only move sideways when housing piece
  {
    name: "Tree side move",
    description: "Tree with housed piece can only move sideways",
    inputFEN: "7bk/8/8/8/TK7",
    lightKing: PieceType.Tree,
    darkKing: PieceType.Bear,
    move: "Light Tree E1->A1",
    expectedFEN: "TK6bk/8/8/8/8",
    expectedDelta: {
      pieceMoved: "Light Tree moved [E1 -> A1]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },

  // ============ WOLF ============
  // Lone Wolf - L-shaped movement when last piece
  {
    name: "Lone wolf",
    description: "Wolf moves in an extended L-shape when alone",
    inputFEN: "7bk/8/WK7/8/8",
    lightKing: PieceType.Wolf,
    darkKing: PieceType.Bear,
    move: "Light Wolf C1->D4",
    expectedFEN: "7bk/8/8/3WK4/8",
    expectedDelta: {
      pieceMoved: "Light Wolf moved [C1 -> D4]",
      turnChanged: true,
      bonusTurn: false,
      gameEnded: false,
    },
  },
];

export const specialMovesSuite: TestSuite = {
  name: "Special Movements",
  description: "Tests for special movement abilities (Lilypad, Traverse, Leap, etc.)",
  tests: specialMoveTests,
};
